import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Target, UserPlus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

export default function JoinCoach() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, joining, success, error, auth_required
  const [message, setMessage] = useState("");

  useEffect(() => {
    joinCoach();
  }, [inviteCode]);

  const joinCoach = async () => {
    try {
      const authRes = await authFetch(`${API}/api/auth/me`);
      if (!authRes.ok) {
        setStatus("auth_required");
        setMessage("You need to sign in first to join a coach.");
        return;
      }

      setStatus("joining");
      const res = await authFetch(`${API}/api/coach/students/join/${inviteCode}`, {
        method: "POST",
      });

      if (res.ok) {
        const data = await res.json();
        setStatus("success");
        setMessage(data.message || "Successfully joined your coach!");
        toast.success("Connected to your coach!");
      } else {
        const err = await res.json().catch(() => ({ detail: "Failed to join" }));
        setStatus("error");
        setMessage(err.detail || "Invalid or expired invite link.");
      }
    } catch (e) {
      setStatus("error");
      setMessage("Network error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <Card className="bg-white border-gray-200 w-full max-w-md">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center mx-auto mb-6">
            <Target className="w-7 h-7 text-[#111]" />
          </div>

          {status === "loading" || status === "joining" ? (
            <>
              <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#555]">Connecting to your coach...</p>
            </>
          ) : status === "success" ? (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-700" />
              </div>
              <h2 className="text-xl font-bold text-[#111] mb-2">Connected!</h2>
              <p className="text-[#555] mb-6">{message}</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
            </>
          ) : status === "auth_required" ? (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-[#111]" />
              </div>
              <h2 className="text-xl font-bold text-[#111] mb-2">Sign In Required</h2>
              <p className="text-[#555] mb-6">{message}</p>
              <Button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full" data-testid="sign-in-btn">
                Sign In to Continue
              </Button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-[#111] mb-2">Link Error</h2>
              <p className="text-[#555] mb-6">{message}</p>
              <Button onClick={() => navigate("/")} variant="outline" className="border-gray-200 text-[#111] hover:bg-gray-100 w-full" data-testid="back-home-btn">
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
